import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, type Resolver, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { useTrans } from '@libs/i18n';
import { SparkleIcon } from '@phosphor-icons/react';

import {
  useExtractionReview,
  useListProfileDocuments,
  useSaveDocumentReview,
} from '../api/tenant-profile-documents-api';
import {
  useCategoryWithValues,
  useTemplateOutline,
} from '../api/tenant-profile-template-api';
import type { ExtractionReviewField, LocalizedTableColumn } from '../api/types';
import {
  ReviewFieldCard,
} from '../components/review/ReviewFieldCard';
import {
  buildReviewDefaults,
  buildReviewSchema,
  type ReviewFormValues,
} from '../components/review/reviewFormSchema';
import { ReviewHeader } from '../components/review/ReviewHeader';
import { ReviewLegend } from '../components/review/ReviewLegend';
import {
  type ReviewStats,
  ReviewStatStrip,
} from '../components/review/ReviewStatStrip';
import {
  coerceScalarForSave,
  normalizeTableCells,
  stripEmptyTableRows,
} from '../organization-profile/utils/fieldValue';
import { ORG_TOKENS } from '../organization-profile/utils/tokens';

/** Per-field workflow flags kept outside the form (not validated values). */
interface FieldFlags {
  verified?: boolean;
  rejected?: boolean;
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

const isEmpty = (v: unknown) =>
  v == null || v === '' || (Array.isArray(v) && v.length === 0);

/**
 * Field types that span the full grid width — mirrors the organization profile
 * (`CategoryEditForm`) so the review grid lays fields out identically: wide
 * tables and textareas get a full row, everything else shares the two columns.
 */
const isWideFieldType = (type: string) => type === 'table' || type === 'textarea';

/**
 * Normalize a `table` value before saving. Mirrors the Organization Profile
 * save: drop fully-empty rows so an added-but-never-filled (or fully cleared)
 * row is treated as non-existent — otherwise the backend rejects it on a
 * required column, blocking the whole save. A non-array (untouched empty table)
 * is passed through unchanged so the backend skips it rather than persisting an
 * empty `[]` override.
 */
function sanitizeTableValue(
  value: unknown,
  columns: LocalizedTableColumn[] | null,
): unknown {
  if (!Array.isArray(value)) return value;
  const stripped = stripEmptyTableRows(value);
  // Coerce list-type cells (`string_list` etc.) that the AI emitted as a
  // comma-joined string into arrays — the backend rejects a bare string.
  return columns && columns.length > 0
    ? normalizeTableCells(stripped, columns)
    : stripped;
}

/**
 * The value as it will be sent to the API for this field. Tables normalize their
 * rows/cells; every other type is coerced to its declared shape (a list column
 * that's a comma string → array, a `"true"` boolean → boolean, an out-of-catalog
 * `select`/`multi_select` option dropped) so an AI-shaped value can't 422 the
 * save. This mirrors the load-time coercion in `buildReviewDefaults`.
 */
function valueForSave(
  field: ExtractionReviewField,
  value: unknown,
  columns: LocalizedTableColumn[] | null,
): unknown {
  return field.type === 'table'
    ? sanitizeTableValue(value, columns)
    : coerceScalarForSave(field.type, value, field.options ?? null);
}

const HUB_PATH = '/tenant-profile/document-hub';

/**
 * Full-page extraction review. Pre-fills typed inputs with the AI values,
 * surfaces confidence + citations, and gates "Approve & save" until every
 * low-confidence field is resolved (edited or verified). Saving mints a profile
 * version synchronously and returns to the hub. Read-only once published.
 */
export const DocumentReviewPage: FC = observer(() => {
  const { t } = useTrans();
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { tenantProfileDocumentId = '' } = useParams<{
    tenantProfileDocumentId: string;
  }>();

  const { data, isLoading, error } = useExtractionReview(tenantProfileDocumentId);
  const { data: docs = [] } = useListProfileDocuments();
  const saveReview = useSaveDocumentReview();

  const doc = useMemo(
    () => docs.find((d) => d.id === tenantProfileDocumentId),
    [docs, tenantProfileDocumentId],
  );

  // Category name now ships localized in the review payload; fall back to the
  // raw key only if the backend couldn't resolve a name.
  const categoryName = data?.categoryName ?? data?.categoryKey ?? null;

  const readOnly = data?.readOnly ?? false;

  // Every field the category defines, including ones the AI found no value for
  // (those render with a "not extracted" hint so the user can still fill them).
  const allFields = useMemo(() => data?.fields ?? [], [data]);

  // Table column structure is owned by the tenant-profile catalog, not the
  // extraction payload — so an empty table still renders its columns. Look up
  // the catalog category for this document and map each table field's columns
  // by key; the extraction payload supplies only the row values.
  const { data: outline } = useTemplateOutline();
  const categoryId = useMemo(
    () =>
      outline?.categories.find((c) => c.key === data?.categoryKey)?.id ?? null,
    [outline, data?.categoryKey],
  );
  const { data: catalogCategory } = useCategoryWithValues(categoryId);
  const tableColumnsByKey = useMemo(() => {
    const map = new Map<string, LocalizedTableColumn[]>();
    for (const f of catalogCategory?.fields ?? []) {
      if (f.type === 'table' && f.columns) map.set(f.key, f.columns);
    }
    return map;
  }, [catalogCategory]);

  // Field descriptions live in the catalog too (the extraction payload omits
  // them) — they feed each field's label tooltip.
  const descriptionByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of catalogCategory?.fields ?? []) {
      if (f.description) map.set(f.key, f.description);
    }
    return map;
  }, [catalogCategory]);

  // ── Form (react-hook-form + Zod), mirroring the Organization Profile ──────
  const schema = useMemo(() => buildReviewSchema(allFields, t), [allFields, t]);
  const defaultValues = useMemo(() => buildReviewDefaults(allFields), [allFields]);

  // Keep a ref so the resolver always validates against the latest schema (it
  // changes when the document's fields load and when the language switches).
  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  const resolver: Resolver<ReviewFormValues> = (values, context, options) =>
    // Cast: the schema is built dynamically from the loaded fields, so its
    // static input type is `unknown`; zodResolver only needs the runtime schema.
    (zodResolver(schemaRef.current as never) as Resolver<ReviewFormValues>)(
      values,
      context,
      options,
    );

  const { control, getValues, watch, reset, trigger, formState } =
    useForm<ReviewFormValues>({
      resolver,
      defaultValues,
      mode: 'onChange',
    });

  // Verify/reject are workflow flags, not validated values — kept outside RHF.
  const [flags, setFlags] = useState<Record<string, FieldFlags>>({});

  // Seed the form once per document. Validate immediately so an invalid AI
  // value (e.g. a cell over its maxLength) blocks the save on load, not only
  // after an edit. Gated on documentId so a background refetch never discards
  // in-progress edits.
  const initializedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!data || initializedFor.current === data.documentId) return;
    initializedFor.current = data.documentId;
    reset(buildReviewDefaults(data.fields));
    setFlags({});
    void trigger();
  }, [data, reset, trigger]);

  const values = watch();

  const editedOf = (f: ExtractionReviewField) =>
    !deepEqual(values[f.fieldId], defaultValues[f.fieldId]);
  const verifiedOf = (f: ExtractionReviewField) => !!flags[f.fieldId]?.verified;
  const rejectedOf = (f: ExtractionReviewField) => !!flags[f.fieldId]?.rejected;
  // A rejected field is excluded from the save, so it no longer needs review.
  const resolvedOf = (f: ExtractionReviewField) =>
    editedOf(f) || verifiedOf(f) || rejectedOf(f);

  const setFlag = (fieldId: string, patch: FieldFlags) =>
    setFlags((prev) => ({ ...prev, [fieldId]: { ...prev[fieldId], ...patch } }));

  const unresolvedLowConfidence = useMemo(
    () => allFields.filter((f) => f.isLowConfidence && !resolvedOf(f)),
    [allFields, values, flags],
  );

  const stats: ReviewStats = useMemo(() => {
    let empty = 0;
    let youEdited = 0;
    let rejected = 0;
    for (const f of allFields) {
      if (isEmpty(f.aiValue)) empty += 1;
      if (editedOf(f)) youEdited += 1;
      if (rejectedOf(f)) rejected += 1;
    }
    return {
      extracted:
        data?.summary.extractedFieldCount ??
        allFields.filter((f) => !isEmpty(f.aiValue)).length,
      total: data?.summary.fieldCount ?? allFields.length,
      needsAttention: unresolvedLowConfidence.length,
      empty,
      youEdited,
      rejected,
    };
  }, [data, allFields, values, flags, unresolvedLowConfidence]);

  // A validation error blocks the save only when the field isn't rejected —
  // rejected fields are dropped from the payload, so their (invalid) value is
  // irrelevant.
  const hasBlockingErrors = Object.keys(formState.errors).some(
    (fieldId) => !flags[fieldId]?.rejected,
  );

  const canSave =
    !readOnly &&
    !saveReview.isPending &&
    unresolvedLowConfidence.length === 0 &&
    !hasBlockingErrors;

  const handleSave = () => {
    if (!data || hasBlockingErrors) return;
    const current = getValues();
    const fieldValues: Record<string, unknown> = {};
    const reviewedFieldKeys: string[] = [];
    for (const f of allFields) {
      if (rejectedOf(f)) {
        // Rejected: excluded from the saved values, but still marked resolved
        // so the backend's low-confidence gate treats it as handled.
        reviewedFieldKeys.push(f.fieldId);
        continue;
      }
      if (editedOf(f) || verifiedOf(f)) reviewedFieldKeys.push(f.fieldId);
      fieldValues[f.fieldId] = valueForSave(
        f,
        current[f.fieldId],
        tableColumnsByKey.get(f.key) ?? f.columns ?? null,
      );
    }
    saveReview.mutate(
      { tenantProfileDocumentId, fieldValues, reviewedFieldKeys },
      {
        onSuccess: () => {
          enqueueSnackbar(t('app.documentHub.review.saved'), { variant: 'success' });
          navigate(HUB_PATH);
        },
        onError: (err) => {
          const status = (err as { response?: { status?: number } })?.response
            ?.status;
          enqueueSnackbar(
            status === 409
              ? t('app.documentHub.review.saveConflict')
              : t('app.documentHub.review.saveError'),
            { variant: 'error' },
          );
        },
      },
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        backgroundColor: 'background.default',
        borderRadius: `${ORG_TOKENS.radiusLg}px`,
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <ReviewHeader
        fileName={data?.fileName ?? doc?.fileName ?? ''}
        doc={doc}
        categoryName={categoryName}
        readOnly={readOnly}
        canSave={canSave}
        saving={saveReview.isPending}
        saveDisabledReason={
          unresolvedLowConfidence.length > 0
            ? t('app.documentHub.review.saveGate', {
                count: unresolvedLowConfidence.length,
              })
            : hasBlockingErrors
              ? t('app.documentHub.review.validationGate')
              : undefined
        }
        onClose={() => navigate(HUB_PATH)}
        onSave={handleSave}
      />

      <Box
        sx={{
          width: '100%',
          px: { xs: 2, md: 4 },
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {error ? (
          <CenteredMessage tone="error">
            {t('app.documentHub.review.loadError')}
          </CenteredMessage>
        ) : isLoading || !data ? (
          <CenteredMessage>{t('app.documentHub.review.loading')}</CenteredMessage>
        ) : (
          <>
            <ReviewLegend />

            <ReviewStatStrip stats={stats} />

            <Box
              sx={{
                backgroundColor: 'background.paper',
                border: 1,
                borderColor: 'grey.300',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  px: { xs: 2, md: 2.5 },
                  py: 1.75,
                  backgroundColor: 'grey.50',
                  borderBottom: 1,
                  borderColor: 'grey.200',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                    <SparkleIcon size={16} weight="fill" color={theme.palette.blue[700]} />
                    <Typography variant="textSM_bold" color="text.primary">
                      {t('app.documentHub.review.sectionTitle')}
                    </Typography>
                  </Box>
                  <Typography variant="textXS_regular" color="text.tertiary">
                    {t('app.documentHub.review.sectionSummary', {
                      extracted: stats.extracted,
                      total: stats.total,
                      empty: stats.empty,
                    })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {unresolvedLowConfidence.length > 0 && (
                    <Typography variant="textXS_semiBold" color="yellow.800">
                      {t('app.documentHub.review.filter.needAttention', {
                        count: unresolvedLowConfidence.length,
                      })}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 1.5,
                  p: { xs: 2, md: 2.5 },
                }}
              >
                {allFields.map((f) => (
                  <Controller
                    key={f.fieldId}
                    name={f.fieldId}
                    control={control}
                    render={({ field: ctrl, fieldState }) => (
                      <ReviewFieldCard
                        field={f}
                        value={ctrl.value}
                        touched={editedOf(f)}
                        verified={verifiedOf(f)}
                        rejected={rejectedOf(f)}
                        error={fieldState.error?.message}
                        disabled={readOnly}
                        fullWidth={isWideFieldType(f.type)}
                        tableColumns={tableColumnsByKey.get(f.key) ?? null}
                        description={descriptionByKey.get(f.key) ?? null}
                        onChange={ctrl.onChange}
                        onVerify={() => setFlag(f.fieldId, { verified: true })}
                        onToggleReject={() =>
                          setFlag(f.fieldId, { rejected: !rejectedOf(f) })
                        }
                      />
                    )}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
});

const CenteredMessage: FC<{ children: React.ReactNode; tone?: 'error' }> = observer(
  ({ children, tone }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <Typography
        variant="textSM_regular"
        color={tone === 'error' ? 'yellow.800' : 'text.tertiary'}
      >
        {children}
      </Typography>
    </Box>
  ),
);
